import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars
import type {FormEvent} from 'inferno';

import {CustomComponent, Link, TooltipWrapper} from '../../../../common/components';
import {Icon} from '../../../../common/components/Icon';
import {subscribe} from '../../../../../replete';
import {UploadDropZone, UploadButton} from '../components/UploadDropZone';
import {SETTINGS} from '../constants';
import {KEY} from '../../../../common/constants';
import {createMediaFormatsList} from '../util';
import {getFileExtension, isValidFileType} from '../../../../common/util/files';
import getFilesArrayFromEvent from '../../../../common/util/getFilesArrayFromEvent';
import {ProgressCircleIndeterminate} from '../../../../common/components/ProgressCircle';
import dialog from '../../../../common/dialog';

import './UploadView.scss';
import {OfficialBadge} from '../../../components/OfficialBadge';

@subscribe({
    isMobile: ['ui.isMobile'],
    makerPage: ['ui.GifMakerPage.makerPage'],
    itemPageCaptioning: ['ui.GifMakerPage.itemPageCaptioning'],
})
export class UploadView extends CustomComponent {
    static debugNamespace = 'UploadView';

    partnerLogos: Record<string, string>;
    editImageDropZoneEl?: HTMLInputElement|null;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.state = {
            ...this.state,
            pageErrorMessages: this.parsePageErrors(),
            stagingInProgress: false,
            validUrl: false,
            dropHover: false,
            url: '',
        };
        this.partnerLogos = {
            gboard: '/assets/img/about/about-us/apipartners/gboard.png',
            linkedin: '/assets/img/about/about-us/apipartners/linkedin.png',
            facebook: '/assets/img/about/about-us/apipartners/facebook.png',
            twitter: '/assets/img/about/about-us/apipartners/twitter.png',
            whatsapp: '/assets/img/about/about-us/apipartners/whatsapp.png',
        };
    }

    componentDidMount() {
        this.showMemesBetaDialog();
    }

    showMemesBetaDialog() {
        const loggedIn = this.context.authService.isLoggedIn();
        const alreadyViewed = this.context.storageService.getItem('staticImageBetaDialogViewed');
        if (loggedIn && !alreadyViewed ) {
            setTimeout(() => dialog.open('static-image-dialog'), 0); // NOTE need timeout or else doesn't trigger
        }
    }

    componentDidUpdate(prevProps: jsx.Props) {
        if (this.props.pageErrorsUpdated) {
            this.setState({
                pageErrorMessages: this.parsePageErrors(),
            });
            this.props.makerPage.setState({pageErrorsUpdated: false});
        }
        if (this.props.queueUpdated) {
            this.setState({
                stagingInProgress: false,
            });
            this.props.makerPage.setState({queueUpdated: false});
        }
    }

    @autobind
    parsePageErrors() {
        return this.props.makerPage.parsePageErrors(this.props.pageErrors);
    }
    @autobind
    resetPageErrors() {
        this.props.makerPage.resetPageErrors();
    }

    @autobind
    urlInput(event: FormEvent<HTMLInputElement>) {
        const url = event.target.value;
        const mediaType = getFileExtension({url});
        const allowedUrlTypes = SETTINGS.ALLOWED_URLS;
        const validGifUrl = isValidFileType({url}, allowedUrlTypes);

        this.setState({
            url,
            validUrl: validGifUrl,
        });
        this.props.pageErrors.length && this.resetPageErrors();

        if (validGifUrl) {
            this.setState({
                stagingInProgress: true,
            });

            this.props.makerPage.trackEvent({
                eventName: 'uploads_selected',
                params: {
                    'actions': 'url',
                    'viewindex': '1', // num urls selected
                    'category': mediaType,
                },
            });

            setTimeout(() => {
                // FIXME: does this need to be in a timeout?
                this.props.makerPage.addItemsToQueue([url]);
            }, 500);
        }
        this.triggerUpdate();
        event.target.focus();
    }

    @autobind
    handleUrlSubmit(event: KeyboardEvent) {
        if (event.keyCode === KEY.ENTER) {
            event.preventDefault(); // remove?
            const pageErrors = [{
                type: 'url-input',
                message: 'Please enter a valid URL.',
            }];
            this.props.makerPage.resetPageErrors(pageErrors);
        }
    }

    @autobind
    fileChange(event: FormEvent<HTMLInputElement>|DragEvent) {
        this.resetPageErrors();
        this.setState({stagingInProgress: true});
        const filesArray: File[] = getFilesArrayFromEvent(event);
        const numFilesSelected = filesArray.length;
        const mediaFormatsList = createMediaFormatsList({
            queue: filesArray,
            type: 'file',
        });

        this.props.makerPage.trackEvent({
            eventName: 'uploads_selected',
            params: {
                'actions': 'file',
                'viewindex': numFilesSelected,
                'category': mediaFormatsList,
            },
        });

        // If an invalid file is dropped onto the target, we won't proceed to
        // the tagging flow, so this needs to be toggled off.
        if (this.state.dropHover) {
            this.setState({dropHover: false});
        }

        if (numFilesSelected) {
            this.setState({stagingInProgress: true}, () => {
                // NOTE: using setState callback to ensure that
                // state.stagingInProgress is updated before processing begins.
                this.props.makerPage.addItemsToQueue(filesArray);
            });
        }
    }

    @autobind
    handleUploadButtonClick(source: string) {
        window.scrollTo({top: 0, behavior: 'smooth'});
        this.trackFileSelectionEvent(source);
    }

    @autobind
    handleUploadDropZoneClick(source: string) {
        window.scrollTo({top: 0, behavior: 'smooth'});
        this.trackFileSelectionEvent(source);
    }

    @autobind
    handleUploadDropZoneDragEnter(source: string) {
        this.setState({dropHover: true});
        this.trackFileSelectionEvent(source);
    }

    @autobind
    handleUploadDropZoneDragLeave() {
        this.setState({dropHover: false});
    }

    @autobind
    trackFileSelectionEvent(source: string) {
        this.props.makerPage.trackEvent({
            eventName: 'splash_upload_tap',
            params: {
                actions: source,
            },
        });
    }

    @autobind
    editImageDropZoneElClick() {
        this.editImageDropZoneEl?.click();
    }

    render() {
        const state = this.state!;
        const gettextSub = this.context.gtService.gettextSub;
        const localizeUrlPath = this.context.gtService.localizeUrlPath;
        const {makerPage, isMobile, pageErrors} = this.props;
        const {dropHover, pageErrorMessages, stagingInProgress} = state!;
        const gettextSubComponent = this.context.gtService.gettextSubComponent;
        if (this.props.itemPageCaptioning.status) {
            return (
                <div className="UploadView"/>
            );
        }
        const validUrlSubmitted = state.validUrl &&
            !pageErrorMessages.length;
        const maxUploads = makerPage ? makerPage.maxUploads() : 0;
        const gifAttributionToolTipMsg = (
            <Link
                to={localizeUrlPath('/official/spongebobmovie')}
                className='gif-attribution-tooltip'
            >
                <img
                    className='profile-image'
                    src='https://c.tenor.com/3/TLrcp1L5rv4AAAfT/tenor.png'
                />
                <span>SpongeBob SquarePants</span>
                <OfficialBadge tooltip={false} flags={'partner'}/>
            </Link>
        );

        const hasUrlError = pageErrors.some(
            (error) => error.type === 'url-input');

        return (
            <div className="UploadView">
                <div className={
                    `upload-scrim-layer${dropHover ? '' : ' hidden'}`
                }>
                    <h1>{gettextSub('Drop to upload!')}</h1>
                    <p>
                        {gettextSub('upload your media files by dropping them here')}
                    </p>
                </div>
                <div className="mask-layer">
                    <div className='left-mask'>
                        <img
                            className="tonal-circle-left"
                            src='/assets/img/gifmaker/tonal-circle-1.svg'/>
                        <div className='circle-gif-mask-wrapper'>
                            <TooltipWrapper
                                size='compact'
                                tooltipMsg={gifAttributionToolTipMsg}
                            >
                                <svg
                                    className='circle-gif-mask'
                                    width="322"
                                    height="322"
                                    viewBox="0 0 322 322"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <defs>
                                        <clipPath id="circleView">
                                            <ellipse
                                                cx="160.938"
                                                cy="160.965"
                                                rx="160.938"
                                                ry="160.965"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                    <image
                                        clipPath="url(#circleView)"
                                        xlinkHref='https://media1.tenor.com/images/5e935f8d2808ca4f45ffd7381bd417a6/tenor.gif?itemid=20224426'
                                    />
                                </svg>
                            </TooltipWrapper>
                        </div>
                    </div>
                    <div className='top-mask'>
                        <img
                            className="tonal-circle-top"
                            src='/assets/img/gifmaker/tonal-circle-2.svg'
                        />
                        <div className='triangle-gif-mask-wrapper'>
                            <TooltipWrapper
                                size='compact'
                                tooltipMsg={gifAttributionToolTipMsg}
                            >
                                <svg
                                    className='triangle-gif-mask'
                                    width="234"
                                    height="213"
                                    viewBox="0 0 234 213"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <defs>
                                        <clipPath id="triangleView">
                                            <path
                                                d="M88.5638 17.3305C101.189 -4.53624 132.75 -4.53628 145.375 17.3304L229.496 163.031C242.12 184.898 226.34 212.232 201.09 212.232H32.849C7.59946 212.232 -8.18144 184.898 4.44332 163.031L88.5638 17.3305Z"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                    <image
                                        clipPath="url(#triangleView)"
                                        xlinkHref='https://media1.tenor.com/images/337af8f2dc0eda3bb46746d4d7cb27d7/tenor.gif?itemid=20224422'
                                    />
                                </svg>
                            </TooltipWrapper>
                        </div>
                    </div>

                    <div className='right-mask'>
                        <img
                            className="tonal-rounded-rect"
                            src='/assets/img/gifmaker/tonal-rounded-rect.svg'/>
                    </div>
                </div>
                <UploadDropZone
                    className={`upload-page${dropHover ? ' dropHover' : ''}`}
                    id='fullpage'
                    fullPage={true}
                    onChange={this.fileChange}
                    onDragEnter={isMobile ?
                        null :
                        () => this.handleUploadDropZoneDragEnter('splash_drag')
                    }
                    onDragLeave={
                        isMobile ?
                            null :
                            this.handleUploadDropZoneDragLeave
                    }
                    disabled={stagingInProgress}
                    multiple={true}
                ></UploadDropZone>
                <section
                    className="hero"
                    role="main"
                    aria-label={gettextSub('Upload and Create')}
                >
                    <div className="container">
                        <div className="content">
                            <div className="value-prop">
                                {
                                    // FIXME: use gettextSubComponent for this
                                    // text block?
                                }
                                <h1 dangerouslySetInnerHTML={{
                                    __html: gettextSub(// eslint-disable-next-line max-len
                                        `<span class="blue">Upload</span> GIFs and stickers, or <span class="blue">create</span> them from MP4s`),
                                }}/>
                                <p>
                                    {gettextSub('If it helps you express, ' +
                                        'this is the place for it. And if it ' +
                                        'needs some fine tuning, you can ' +
                                        'trim, crop, and caption before ' +
                                        'sharing to Twitter, Facebook, and ' +
                                        'just about everywhere in between.')}
                                </p>
                            </div>

                            <div
                                className='uploader-card'
                                role="region"
                                aria-label={gettextSub('Uploader')}
                            >
                                <UploadDropZone
                                    className={`upload-file-section`}
                                    id='splash'
                                    onClick={() => this.handleUploadDropZoneClick('splash')}
                                    onChange={this.fileChange}
                                    disabled={stagingInProgress}
                                    multiple={true}
                                >
                                    <img
                                        className='file-illustration'
                                        src='/assets/img/gifmaker/file-illustration.svg'
                                    />
                                    { isMobile ?
                                        <h3>
                                            {gettextSub('Tap to Upload Here')}
                                        </h3> :
                                        <h3>
                                            {gettextSub('Drag & Drop to Upload Here')}
                                        </h3>
                                    }

                                    <p className="upload-details">
                                        {gettextSubComponent('Up to {maxUploads} GIFs, MP4s, PNGs, JPGs', {
                                            maxUploads: <span
                                                className='max-uploads-text'
                                            >
                                                {maxUploads}
                                            </span>,
                                        })}
                                    </p>

                                    { stagingInProgress &&
                                            <ProgressCircleIndeterminate
                                                diameter={40}
                                                strokeWidthRatio={.1}
                                                color={'#007add'}
                                                animationDuration={1500}
                                            />
                                    }
                                    <UploadButton
                                        id='upload-file-section-button'
                                        onClick={() => this.handleUploadButtonClick('splash_button')}
                                        onChange={this.fileChange}
                                        disabled={stagingInProgress}
                                        multiple={true}
                                    >
                                        <span className='create-gifs-button-label'>
                                            <span className='text'>{gettextSub('Browse Files')}</span>
                                        </span>
                                    </UploadButton>
                                </UploadDropZone>

                                <div
                                    className={`upload-url-section${hasUrlError ? ' error' : ''}`}
                                >
                                    <h3>{gettextSub('URL Upload')}</h3>
                                    <p className='url-upload-details'>
                                        {gettextSub('Any GIF, MP4, PNG, or JPG URL')}
                                    </p>
                                    <div className='input-component'>
                                        <input
                                            id="upload_url"
                                            type="text"
                                            value={state.url}
                                            onInput={this.urlInput}
                                            onKeyDown={this.handleUrlSubmit}
                                            onBlur={this.resetPageErrors}
                                            disabled={stagingInProgress}
                                            className={`${validUrlSubmitted ?
                                                'feedback-valid ' : ''}${
                                                hasUrlError ? ' error' : ''}`}
                                            name='url'
                                            placeholder={gettextSub('Paste a media URL')}
                                            autoComplete='off'
                                            spellCheck={false}/>
                                        <div className="error-section">
                                            { !stagingInProgress &&
                                                pageErrorMessages.map((
                                                    msg: string) => {
                                                    return <div key={msg}>
                                                        {msg}
                                                    </div>;
                                                })}
                                        </div>
                                    </div>
                                </div>
                                <Link
                                    className="legal-terms"
                                    to={localizeUrlPath('/legal-terms')}
                                    external={true}
                                >{gettextSub('Terms & Privacy')}</Link>
                            </div>

                        </div>
                    </div>
                </section>

                <section className="overview" role="region" aria-label={gettextSub('Getting Started')}>
                    <div className="container">
                        <h2>
                            {gettextSub('Getting started on Tenor')}
                        </h2>
                        <div className="content">
                            <div className="item">
                                <div className="img-wrapper">
                                    <img
                                        className="build"
                                        src='/assets/img/gifmaker/library.png'
                                    />
                                </div>
                                <h3>{gettextSub('Build your Library')}</h3>
                                <p>{gettextSub('Upload and edit your GIFs, stickers, or short MP4s to your Tenor profile to share with friends and family')}</p>
                            </div>
                            <div className="item">
                                <div className="img-wrapper">
                                    <img
                                        className="discover"
                                        src='/assets/img/gifmaker/tag.png'
                                    />
                                </div>
                                <h3>{
                                    // Translators: This is a section
                                    // Translators: heading/CTA explaining to
                                    // Translators: users that tagging gifs
                                    // Translators: makes them discoverable
                                    // Translators: through our search.
                                    gettextSub('Tag and Discovery')
                                }</h3>
                                <p>
                                    {gettextSub('Tag your GIFs to be discovered & Shared. Search for your content on Tenor’s partners, including Gboard, Twitter, and many more.')}
                                </p>
                            </div>
                            <div className="item">
                                <div className="img-wrapper">
                                    <img className="share" src='/assets/img/gifmaker/share.png'/>
                                </div>
                                <h3>{gettextSub('Share with millions')}</h3>
                                <p>
                                    {gettextSub('Get notified on how your GIFs are performing and how often they\'ve been searched and shared.')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="create" role="region" aria-label={gettextSub('Creation Tools')}>
                    <div className="container">
                        <div className="content">
                            <div className="copy-section">
                                <h2>{gettextSub('Create your own GIFs')}</h2>
                                <p>
                                    {gettextSub('Creating GIFs is fun and easy! Edit the GIFs and MP4s that you upload to Tenor by trimming, cropping, and adding custom captions to your content')}
                                </p>
                                <UploadButton
                                    className='upload-button'
                                    id='create-section-button'
                                    onClick={
                                        () => this.handleUploadButtonClick('edit_info')}
                                    onChange={this.fileChange}
                                    disabled={state.stagingInProgress}
                                    multiple={true}
                                >
                                    <span className='create-gifs-button-label'>
                                        <img
                                            src="/assets/img/gifmaker/palette-icon.svg"
                                        />
                                        <span className='text'>
                                            {gettextSub('Create GIFs')}
                                        </span>
                                    </span>
                                </UploadButton>
                            </div>

                            <div className="image-section">
                                <div className="create-gif-animation-wrapper">
                                    <div className="create-gif-animation">
                                        <UploadDropZone
                                            id='create-section-image'
                                            onClick={
                                                () => this.handleUploadDropZoneClick('edit_image')}
                                            onChange={this.fileChange}
                                            disabled={
                                                state.stagingInProgress}
                                            multiple={true}
                                            reference={(
                                                el: HTMLInputElement|null) => {
                                                this.editImageDropZoneEl = el;
                                            }}
                                        />
                                        <img
                                            src={'/assets/img/gifmaker/create.gif'}
                                            onClick={
                                                this.editImageDropZoneElClick}
                                        />
                                        <Icon
                                            name="trim-icon"
                                            className="hovering-tool-icon"
                                            onClick={
                                                this.editImageDropZoneElClick}
                                        />
                                        <Icon
                                            name="crop-icon"
                                            className="hovering-tool-icon"
                                            onClick={
                                                this.editImageDropZoneElClick}
                                        />
                                        <Icon
                                            name="caption-icon"
                                            className="hovering-tool-icon"
                                            onClick={
                                                this.editImageDropZoneElClick}
                                        />
                                        <Icon
                                            name="pencil-icon"
                                            onClick={
                                                this.editImageDropZoneElClick}
                                        />
                                    </div>
                                    <div
                                        className="create-gif-animation-border">
                                        <div className="image-border"></div>
                                        <div className="pencil-border"></div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                <section
                    className="share"
                    role="complementary"
                    aria-label={gettextSub('Share Anywhere')}
                >
                    <div className="container">
                        <div className="content">
                            <div className="item-left">
                                <div className='phone'>
                                    <div className='phone-body'>
                                        <div className='phone-body2'></div>
                                    </div>
                                    <div className='speaker-top'></div>
                                    <div className='lock-button'></div>
                                    <div className='volume-button'></div>
                                </div>
                                <video
                                    src={'/assets/img/gifmaker/phone.mp4'}
                                    muted={true}
                                    playsInline={true}
                                    autoPlay={true}
                                    loop={true}
                                />
                            </div>

                            <div className="item-right">
                                <h2>{gettextSub('Share anywhere')}</h2>
                                <p>
                                    {gettextSubComponent('Access GIFs you uploaded anytime from Tenor products including the Tenor website and {appStoreLink}. Tenor also powers GIF search for Gboard, Facebook, Twitter, Line, WhatsApp, and more!', {
                                        appStoreLink: <Link
                                            className='appstore-link'
                                            to="https://apps.apple.com/app/apple-store/id917932200?pt=39040802&ct=GifMaker&mt=8"
                                            external={true}
                                        >{gettextSub('GIF Keyboard')}</Link>,
                                    })}
                                </p>
                                <UploadButton
                                    className='upload-button'
                                    id='share-section-button'
                                    onClick={() => this.handleUploadButtonClick('share_info')}
                                    onChange={this.fileChange}
                                    disabled={state.stagingInProgress}
                                    multiple={true}
                                >
                                    <span>{gettextSub('Get Started')}</span>
                                </UploadButton>
                                <div className="partner-logos">
                                    <div className="logo-row">
                                        <img
                                            className="whatsapp"
                                            src={this.partnerLogos.whatsapp}
                                        />
                                        <img
                                            className="gboard"
                                            src={this.partnerLogos.gboard}
                                        />
                                        <img
                                            className="linkedin"
                                            src={this.partnerLogos.linkedin}
                                        />
                                    </div>
                                    <div className="logo-row">
                                        <img
                                            className="facebook"
                                            src={this.partnerLogos.facebook}
                                        />
                                        <img
                                            className="twitter"
                                            src={this.partnerLogos.twitter}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
